import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api/client';
import { useApi } from '../api/useApi';

type Status = 'Active' | 'Considering' | 'Inactive' | 'All';

const BADGE: Record<string, string> = {
  Active: 'badge badge-confirmed',
  Considering: 'badge badge-awaiting',
  Inactive: 'badge badge-declined',
};

const OUTREACH_BADGE: Record<string, string> = {
  Confirmed: 'badge badge-confirmed',
  'Invite Sent': 'badge badge-awaiting',
  'Awaiting RSVP': 'badge badge-awaiting',
  'No Response': 'badge badge-declined',
  Declined: 'badge badge-declined',
  'N/A': 'badge',
};

const FILTER_ORDER: Status[] = ['All', 'Active', 'Considering', 'Inactive'];

// Outreach-status filter values, in the order they appear in the dropdown.
// Mirrors the OUTREACH_BADGE keys; 'All' is the no-filter default.
const OUTREACH_FILTER_ORDER = ['All', 'Confirmed', 'Invite Sent', 'Awaiting RSVP', 'No Response', 'Declined', 'N/A'] as const;
type OutreachFilter = typeof OUTREACH_FILTER_ORDER[number];

// Partner type, derived from the FI's industry. VC/EIR are their own industries;
// everything else is a standard financial institution.
type PartnerType = 'All' | 'FI' | 'VC' | 'EIR';
const TYPE_LABELS: Record<Exclude<PartnerType, 'All'>, string> = {
  FI: 'FIs', VC: 'VCs', EIR: 'EIRs',
};
function partnerType(industry?: string | null): Exclude<PartnerType, 'All'> {
  if (industry === 'Venture Capitalist') return 'VC';
  if (industry === 'Executive In Residence') return 'EIR';
  return 'FI';
}

const INDUSTRY_OPTIONS = [
  'Banking & Payments', 'Capital Markets', 'Insurance', 'Venture Capitalist', 'Executive In Residence',
];
const STATUS_OPTIONS = ['Active', 'Considering', 'Inactive'];

function CreateFIModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [industry, setIndustry] = useState('');
  const [relationshipStatus, setRelationshipStatus] = useState('Active');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };

  const submit = async () => {
    if (!name.trim()) { setErr('Name is required.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      const created = await API.createFI({
        name: name.trim(),
        shortName: shortName.trim() || null,
        industry: industry || null,
        relationshipStatus: relationshipStatus || null,
      });
      onCreated(created.id);
    } catch (e: any) {
      setErr(e?.message || 'Failed to create FI.');
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
    >
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 440, maxWidth: '90vw', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid #eee', fontWeight: 700, fontSize: 16 }}>Add Financial Institution</div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input autoFocus style={input} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bank of America" />
          </div>
          <div>
            <label style={labelStyle}>Short name</label>
            <input style={input} value={shortName} onChange={e => setShortName(e.target.value)} placeholder="e.g. BofA" />
          </div>
          <div>
            <label style={labelStyle}>Industry</label>
            <select style={input} value={industry} onChange={e => setIndustry(e.target.value)}>
              <option value="">— Select —</option>
              {INDUSTRY_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select style={input} value={relationshipStatus} onChange={e => setRelationshipStatus(e.target.value)}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {err && <div className="phil-error">{err}</div>}
        </div>
        <div style={{ padding: '12px 20px 18px', display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #eee' }}>
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create FI'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface AppOption { id: string; company: string; industry?: string; }

// Applications still being filled out — excluded from the assign modal so an FI
// can only be matched to a submitted application. Mirrors the backend's
// _INPROGRESS_STATUSES ({"In Progress", "Draft"}); anything else counts as submitted.
const UNSUBMITTED_STATUSES = new Set(['Draft', 'In Progress']);

// Bulk-assign startup applications to a single FI. The inverse of the pipeline's
// AssignmentModal (which assigns FIs to one application). Set-replace: the FI's
// current assignments are pre-checked and saving diffs adds/removes server-side.
function AssignStartupsModal({ fi, cycle, onClose, onSaved }: {
  fi: { id: string; name: string };
  cycle: string;
  onClose: () => void;
  onSaved: (fiId: string, count: number) => void;
}) {
  const [apps, setApps] = useState<AppOption[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    let live = true;
    Promise.all([API.internalApplications(cycle), API.fiAssignments(fi.id)])
      .then(([data, current]) => {
        if (!live) return;
        setApps((data?.applications ?? [])
          .filter((a: any) => !UNSUBMITTED_STATUSES.has(a.status))
          .map((a: any) => ({ id: a.id, company: a.company, industry: a.industry })));
        setSelected(new Set<string>(current?.applicationIds ?? []));
        setLoading(false);
      })
      .catch(err => { if (live) { setError(err?.message ?? 'Failed to load applications'); setLoading(false); } });
    return () => { live = false; };
  }, [fi.id]);

  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const shownApps = apps.filter(a =>
    !filter ||
    a.company.toLowerCase().includes(filter.toLowerCase()) ||
    a.id.toLowerCase().includes(filter.toLowerCase()),
  );

  // Select-all operates on the currently shown (filtered) list so it never
  // silently touches startups the admin has hidden with the filter box.
  const allShownSelected = shownApps.length > 0 && shownApps.every(a => selected.has(a.id));
  const toggleAllShown = () => setSelected(prev => {
    const next = new Set(prev);
    if (allShownSelected) shownApps.forEach(a => next.delete(a.id));
    else shownApps.forEach(a => next.add(a.id));
    return next;
  });

  const save = () => {
    setSaving(true);
    setError(null);
    API.setFiAssignments(fi.id, Array.from(selected))
      .then(res => onSaved(fi.id, (res?.assignedApplications ?? []).length))
      .catch(err => { setError(err?.message ?? 'Save failed'); setSaving(false); });
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 10, width: 460, maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}
      >
        <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid #eee' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Assign Startups</div>
          <div style={{ color: '#888', fontSize: 13, marginTop: 2 }}>{fi.name} · {cycle}</div>
        </div>

        <div style={{ padding: '12px 20px 0' }}>
          <input
            className="search-input"
            style={{ width: '100%', boxSizing: 'border-box' }}
            placeholder="⌕  Filter startups…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
        </div>

        {!loading && shownApps.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 20px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown} />
            <span style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>
              {allShownSelected ? 'Deselect all' : 'Select all'}{filter ? ' (filtered)' : ''} ({shownApps.length})
            </span>
          </label>
        )}

        <div style={{ overflowY: 'auto', padding: '10px 20px', flex: 1 }}>
          {loading && <div style={{ color: '#aaa', padding: 16, textAlign: 'center' }}>Loading…</div>}
          {!loading && shownApps.length === 0 && <div style={{ color: '#aaa', padding: 16, textAlign: 'center' }}>No startups found</div>}
          {!loading && shownApps.map(a => (
            <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} />
              <span style={{ fontWeight: 600 }}>{a.company}</span>
              {a.industry && a.industry !== '—' && <span style={{ color: '#aaa', fontSize: 12 }}>· {a.industry}</span>}
            </label>
          ))}
        </div>

        {error && <div className="phil-error" style={{ margin: '0 20px' }}>{error}</div>}

        <div style={{ padding: '12px 20px', borderTop: '1px solid #eee', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ color: '#888', fontSize: 13 }}>{selected.size} selected</span>
          <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || loading}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export default function FeedbackPriorities() {
  const navigate = useNavigate();
  // Default to Active so the working list stays clean now that inactive/legacy
  // partners are seeded from the Stakeholder Directory. Inactive/Considering/All
  // are all one click away via the filter tabs.
  const [filter, setFilter] = useState<Status>('Active');
  const [typeFilter, setTypeFilter] = useState<PartnerType>('All');
  const [search, setSearch] = useState('');
  const [outreachFilter, setOutreachFilter] = useState<OutreachFilter>('All');
  const [showAdd, setShowAdd] = useState(false);
  // FI whose startup assignments are being edited (null = modal closed).
  const [assigning, setAssigning] = useState<{ id: string; name: string } | null>(null);
  // Toast-style confirmation after a bulk save, keyed by FI id.
  const [savedMsg, setSavedMsg] = useState('');
  // Cohort follows the Current Program Year set by admins in /admin/settings.
  const { data: settings } = useApi<any>(() => API.getSettings());
  const cycle = settings?.currentYear ? `FY${String(settings.currentYear).slice(-2)}` : 'FY26';
  const { data: fis, loading, error } = useApi<any[]>(() => API.listFIs(cycle), [cycle]);

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error || !fis) return <div className="phil-error">Unable to load FI partners. {error}</div>;

  // Counts derived from real data (not hardcoded).
  const counts: Record<string, number> = { All: fis.length };
  fis.forEach((fi: any) => { counts[fi.relationshipStatus] = (counts[fi.relationshipStatus] ?? 0) + 1; });

  // Partner-type counts (FI/VC/EIR), derived from the data.
  const typeCounts: Record<string, number> = {};
  fis.forEach((fi: any) => {
    const t = partnerType(fi.industry);
    typeCounts[t] = (typeCounts[t] ?? 0) + 1;
  });

  // Outreach-status counts, derived from the data (like the Status/Type counts).
  // FIs with no outreach status fall under the 'N/A' bucket.
  const outreachCounts: Record<string, number> = {};
  fis.forEach((fi: any) => {
    const o = fi.outreachStatus || 'N/A';
    outreachCounts[o] = (outreachCounts[o] ?? 0) + 1;
  });

  const shown = fis.filter((fi: any) => {
    const matchFilter = filter === 'All' || fi.relationshipStatus === filter;
    const matchType = typeFilter === 'All' || partnerType(fi.industry) === typeFilter;
    const matchSearch = fi.name.toLowerCase().includes(search.toLowerCase());
    const matchOutreach = outreachFilter === 'All' || (fi.outreachStatus || 'N/A') === outreachFilter;
    return matchFilter && matchType && matchSearch && matchOutreach;
  });

  // Table columns in order. The two trailing '' entries are the action columns
  // (Assign Startups button, row arrow). colSpan for the empty state derives from
  // this so it always spans the full width.
  const columns = [
    'FI Partner', 'Industry', 'Exec Sponsor', 'Chaperone',
    `Priority Call (${cycle})`, 'Outreach Status', 'Status', '', '',
  ];

  const selectStyle: React.CSSProperties = {
    padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6,
    fontSize: 13, background: '#fff', color: '#374151',
  };

  return (
    <>
      <div className="page-header">

        <h1>Feedback &amp; Priorities</h1>
        <p className="subtitle">Feedback &amp; Priority Discussions · {cycle}</p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <input
          className="search-input"
          style={{ maxWidth: 260 }}
          placeholder="⌕  Search…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          style={selectStyle}
          value={filter}
          onChange={e => setFilter(e.target.value as Status)}
        >
          {FILTER_ORDER.map(s => (
            <option key={s} value={s}>
              {s === 'All' ? `All statuses (${counts.All ?? 0})` : `${s} (${counts[s] ?? 0})`}
            </option>
          ))}
        </select>
        <select
          style={selectStyle}
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value as PartnerType)}
        >
          <option value="All">All types ({fis.length})</option>
          {(['FI', 'VC', 'EIR'] as const).map(t => (
            <option key={t} value={t}>{TYPE_LABELS[t]} ({typeCounts[t] ?? 0})</option>
          ))}
        </select>
        <select
          style={selectStyle}
          value={outreachFilter}
          onChange={e => setOutreachFilter(e.target.value as OutreachFilter)}
          title="Filter FI partners by outreach status"
        >
          {OUTREACH_FILTER_ORDER.map(o => (
            <option key={o} value={o}>
              {o === 'All' ? `All outreach (${fis.length})` : `${o} (${outreachCounts[o] ?? 0})`}
            </option>
          ))}
        </select>
        <button
          className="btn btn-outline btn-sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => setShowAdd(true)}
        >
          + Add FI
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => navigate('/feedback-priorities/scoring')}
        >
          Priority Scoring Matrix
        </button>
      </div>

      {showAdd && (
        <CreateFIModal
          onClose={() => setShowAdd(false)}
          onCreated={id => navigate(`/fi/${id}`)}
        />
      )}

      {assigning && (
        <AssignStartupsModal
          fi={assigning}
          cycle={cycle}
          onClose={() => setAssigning(null)}
          onSaved={(_fiId, count) => {
            setAssigning(null);
            setSavedMsg(`Assigned ${count} startup${count === 1 ? '' : 's'} to ${assigning.name}.`);
            setTimeout(() => setSavedMsg(''), 4000);
          }}
        />
      )}

      {savedMsg && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1100,
          background: '#111827', color: '#fff', padding: '10px 16px',
          borderRadius: 8, fontSize: 13, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        }}>{savedMsg}</div>
      )}

      <div className="card">
        <table>
          <thead>
            <tr>
              {columns.map((label, i) => <th key={i}>{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map(fi => (
              <tr key={fi.id} className="row-link" onClick={() => navigate(`/fi/${fi.id}`)}>
                <td className="font-semibold">{fi.name}</td>
                <td style={{ color: '#4b5563' }}>{fi.industry || '—'}</td>
                <td>{fi.execSponsor || '—'}</td>
                <td>{fi.chaperone || '—'}</td>
                <td style={{ color: '#4b5563', whiteSpace: 'nowrap' }}>{fi.callDate || '—'}</td>
                <td>{fi.outreachStatus ? <span className={OUTREACH_BADGE[fi.outreachStatus] || 'badge'}>{fi.outreachStatus}</span> : '—'}</td>
                <td>{fi.relationshipStatus ? <span className={BADGE[fi.relationshipStatus] || 'badge'}>{fi.relationshipStatus}</span> : '—'}</td>
                <td onClick={e => { e.stopPropagation(); setAssigning({ id: fi.id, name: fi.name }); }}>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={e => { e.stopPropagation(); setAssigning({ id: fi.id, name: fi.name }); }}
                  >
                    Assign Startups
                  </button>
                </td>
                <td className="row-arrow">→</td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={columns.length} style={{ textAlign: 'center', color: '#9ca3af', padding: 28 }}>No results</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/feedback-priorities/scoring')}>
          View H/M/L Scoring Matrix →
        </button>
      </div>
    </>
  );
}
